import AnalyticsClientError from './AnalyticsClientError.js';

/**
 * The endpoint to send analytics data to.
 */
const ANALYTICS_ENDPOINT = 'https://a.near.st/t';

/**
 * Analytics client.
 * Allows our front-ends to track metrics and send them to the analytics endpoint.
 *
 * @class
 */
class AnalyticsClient {
	/**
	 * Create a new AnalyticsClient instance.
	 *
	 * @typedef {object} AnalyticsClientConstructorOptions
	 * @property {string} domain - The namespace to save metrics under
	 * @property {boolean} [verbose] - Whether to log verbose output to the console
	 * @property {string} [proxyEndpoint] - Optional proxy endpoint to use, rather than the default API endpoint
	 *
	 * @param {AnalyticsClientConstructorOptions} options
	 */
	constructor(options) {
		if (!options) {
			throw new AnalyticsClientError('Options parameter is required to create an instance of AnalyticsClient.');
		}
		if (!options.domain) {
			throw new AnalyticsClientError('Domain parameter is required to create an instance of AnalyticsClient.');
		}

		this.domain = options.domain;
		this.verbose = options.verbose || false;
		this.endpoint = options.proxyEndpoint || ANALYTICS_ENDPOINT;
		this.defaultAttributes = {};
	}

	/**
	 * Set default attributes to be sent with every event.
	 *
	 * @param {AnalyticsEventAttributes} attributes
	 * @returns {void}
	 */
	setDefaultAttributes(attributes) {
		this.defaultAttributes = {
			...this.defaultAttributes,
			...attributes
		};
	}

	/**
	 * Track an event.
	 *
	 * @typedef {Object.<string, any>} AnalyticsEventAttributes
	 * @property {string} [organisationId] The organisation ID
	 * @property {string} [shopId] The shop ID
	 *
	 * @param {string} metric The event name
	 * @param {AnalyticsEventAttributes} [attributes] Additional attributes to send with the event
	 *
	 * @returns {Promise<void>}
	 */
	async track(metric, attributes = {}) {
		/** @type {AnalyticsEventAttributes} */
		const mergedAttributes = {
			...this.defaultAttributes,
			...this.#gatherContextAttributes(),
			...attributes
		};

		const { organisationId, shopId, trackingConsent, ...customAttributes } = mergedAttributes;

		if (!organisationId && !shopId) {
			throw new AnalyticsClientError('Organisation ID or Shop ID is required to track a metric.');
		}

		const headers = {};
		if (trackingConsent) headers['Tracking-Consent'] = 'consent';

		await this.#sendRequest(
			{
				metric,
				domain: this.domain,
				shopId,
				organisationId,
				attributes: customAttributes
			},
			{ headers }
		);

		this.#log('info', `Tracked metric: ${metric}`, {
			domain: this.domain,
			shopId,
			organisationId,
			attributes: customAttributes
		});
	}

	/**
	 * Gather default attributes from the current client context.
	 *
	 * @returns {AnalyticsEventAttributes}
	 */
	#gatherContextAttributes() {
		return {
			date: new Date().toISOString().substring(0, 10),
			timestamp: new Date().toISOString()
		};
	}

	/**
	 * Send a request to the analytics endpoint with retry on failure.
	 *
	 * @param {Object} requestBody The request body to send to the endpoint
	 * @param {Object} [config] Config to attach to the request
	 * @param {Object} [config.headers] Additional headers to add to the request
	 * @param {number} [retries] Number of retry attempts remaining
	 * @returns {Promise<void>}
	 */
	#sendRequest(requestBody, config = {}, retries = 2) {
		const headers = new Headers({
			'content-type': 'application/json',
			...config.headers
		});

		// If we're running in the back-end, add a user agent
		// to avoid being blocked by the analytics endpoint
		const isNode = typeof process !== 'undefined' && process.versions?.node;
		const userAgent = requestBody.attributes?.userAgent;

		if (userAgent) {
			headers.set('user-agent', userAgent);
		} else if (isNode) {
			headers.set('user-agent', 'Mozilla/5.0 (nearst-analytics-client)');
		}

		// keepalive lets the browser complete the request after the page navigates —
		// without it, tracking fired immediately before router.push/window.location is
		// cancelled and the event is lost (NEA-6114).
		const fetchOptions = {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify(requestBody)
		};
		if (!isNode) {
			fetchOptions.keepalive = true;
		}

		return fetch(this.endpoint, fetchOptions)
			.then(() => {
				this.#log('debug', 'Successfully sent metric to analytics endpoint');
			})
			.catch((error) => {
				if (retries > 0) {
					const delay = (3 - retries) * 1000;
					return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
						this.#sendRequest(requestBody, config, retries - 1)
					);
				}
				this.#log('warn', 'Failed to send metric to analytics endpoint after retries', error);
			});
	}

	/**
	 * Log a message to the console, if the client is in verbose mode.
	 *
	 * @param {'info'|'log'|'warn'|'debug'} type
	 * @param  {...any} message
	 * @returns {void}
	 */
	#log(type, ...message) {
		if (!this.verbose) return;
		console[type]('[AnalyticsClient]', ...message);
	}
}

export default AnalyticsClient;
